import type { Breakdown, Chain, Deliverable, ISODate, Period } from "./types";
import { BREAKDOWNS, LOE_CHAIN } from "./types";
import { periodIndexOf } from "./calendar";

export type Severity = "error" | "warning";

export interface Issue {
  severity: Severity;
  code:
    | "skippedStep" // rule 6
    | "earnedBackwards" // rule 6
    | "beforeCalendar" // rule 18
    | "afterCalendar" // rule 18
    | "reforecast" // R29: a date that isn't met gets re-forecast
    | "earnedInFuture"
    | "plannedOutOfOrder"
    | "badWeight"
    | "unknownChain"
    | "chainMismatch"
    | "unknownCode"
    | "changeOnComplete"; // pending Cesar: change order on a finished deliverable
  deliverable: string;
  step?: number; // 1-based
  field?: "planned" | "forecast" | "earned" | Breakdown;
  message: string;
}

export interface ValidateContext {
  periods: Period[];
  chains?: Map<string, Chain>;
  /** Index of the current (open) period. Forecast dates before it are stale. */
  currentIdx?: number;
  knownCodes?: Partial<Record<Breakdown, Set<string>>>;
}

export function validateDeliverable(d: Deliverable, ctx: ValidateContext): Issue[] {
  const out: Issue[] = [];
  const add = (i: Omit<Issue, "deliverable">) => out.push({ ...i, deliverable: d.code });
  const { periods } = ctx;

  if (!(d.weight >= 0) || !isFinite(d.weight)) add({ severity: "error", code: "badWeight", message: "Weight must be a number of 0 or more" });

  if (ctx.chains && d.chainCode !== LOE_CHAIN) {
    const ch = ctx.chains.get(d.chainCode);
    if (!ch) add({ severity: "error", code: "unknownChain", message: `Chain ${d.chainCode} does not exist` });
    else if (ch.steps.length !== d.steps.length || ch.steps.some((s, i) => s.milestone !== d.steps[i]?.milestone))
      add({ severity: "error", code: "chainMismatch", message: `Milestones don't match chain ${d.chainCode}` });
  }

  const checkRange = (dt: ISODate | null | undefined, step: number, field: "planned" | "forecast" | "earned") => {
    if (!dt || !periods.length) return;
    const pi = periodIndexOf(periods, dt);
    if (pi === "before")
      add({ severity: "error", code: "beforeCalendar", step, field, message: `${field} date ${dt} is before the calendar start (${periods[0].start})` });
    if (pi === "after")
      add({ severity: "error", code: "afterCalendar", step, field, message: `${field} date ${dt} is after the calendar end (${periods[periods.length - 1].cutoff}); re-forecast` });
  };

  let prevEarned: ISODate | null = null;
  let prevPlanned: ISODate | null = null;
  let gap = false;
  d.steps.forEach((s, i) => {
    const n = i + 1;
    checkRange(s.planned, n, "planned");
    checkRange(s.forecast, n, "forecast");
    checkRange(s.earned, n, "earned");
    if (s.earned) {
      if (gap) add({ severity: "error", code: "skippedStep", step: n, field: "earned", message: `Step ${n} (${s.milestone}) is earned but an earlier step is not` });
      if (prevEarned && s.earned < prevEarned)
        add({ severity: "error", code: "earnedBackwards", step: n, field: "earned", message: `Step ${n} earned ${s.earned}, before the previous step (${prevEarned})` });
      prevEarned = s.earned;
    } else gap = true;
    if (s.planned) {
      if (prevPlanned && s.planned < prevPlanned)
        add({ severity: "warning", code: "plannedOutOfOrder", step: n, field: "planned", message: `Planned date for step ${n} is before the previous step's` });
      prevPlanned = s.planned;
    }
    const cur = ctx.currentIdx !== undefined ? periods[ctx.currentIdx] : undefined;
    if (cur) {
      if (!s.earned && s.forecast && s.forecast < cur.start)
        add({ severity: "warning", code: "reforecast", step: n, field: "forecast", message: `Forecast ${s.forecast} has passed without being earned; re-forecast` });
      if (s.earned && s.earned > cur.cutoff)
        add({ severity: "warning", code: "earnedInFuture", step: n, field: "earned", message: `Earned date ${s.earned} is after the current cutoff ${cur.cutoff}` });
    }
    if (ctx.knownCodes && s.codes) {
      for (const b of BREAKDOWNS) {
        const v = s.codes[b];
        const known = ctx.knownCodes[b];
        if (v && known && !known.has(v)) add({ severity: "error", code: "unknownCode", step: n, field: b, message: `${b.toUpperCase()} ${v} is not in the breakdown` });
      }
    }
  });

  if (ctx.knownCodes) {
    for (const b of BREAKDOWNS) {
      const v = d.codes[b];
      const known = ctx.knownCodes[b];
      if (v && known && !known.has(v)) add({ severity: "error", code: "unknownCode", field: b, message: `${b.toUpperCase()} ${v} is not in the breakdown` });
    }
  }

  if (d.weightChanges?.length && d.steps.length && d.steps[d.steps.length - 1].earned && periods.length) {
    const last = d.steps[d.steps.length - 1].earned!;
    const pi = periodIndexOf(periods, last);
    const doneIdx = typeof pi === "number" ? pi : pi === "before" ? 0 : Infinity;
    for (const c of d.weightChanges) {
      const ci = periods.findIndex((p) => p.number === c.periodNumber);
      if (ci >= 0 && ci > doneIdx)
        add({ severity: "warning", code: "changeOnComplete", message: `Weight change ${c.ref ?? ""} after the deliverable reached 100%. Its earned value does not move (question pending with Cesar)` });
    }
  }
  return out;
}

/** Rule 6: reject earned-date edits that would skip a step or go backwards. Returns the offending issues only. */
export function earnedOrderIssues(d: Deliverable): Issue[] {
  return validateDeliverable(d, { periods: [] }).filter((i) => i.code === "skippedStep" || i.code === "earnedBackwards");
}
